// src/components/forms/CallRulesSection.jsx
import React from 'react';
import Button from '../common/Button';
import { DAYS_OF_WEEK, FREQUENCY_OPTIONS, INTERVAL_OPTIONS, DAYS_OF_MONTH } from '../../constants/formConstants';

const selectClassName = "block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";

const CallRulesSection = ({ callRules, onCallRuleChange, onAddRule, onRemoveRule, error }) => {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleDayToggle = (index, dayValue, currentDays) => {
    const updated = currentDays.includes(dayValue)
      ? currentDays.filter(d => d !== dayValue)
      : [...currentDays, dayValue];
    onCallRuleChange(index, 'days', updated);
  };

  const handleDayOfMonthToggle = (index, dayNum, currentDays) => {
    const updated = currentDays.includes(dayNum)
      ? currentDays.filter(d => d !== dayNum)
      : [...currentDays, dayNum];
    onCallRuleChange(index, 'days_of_month', updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Call Schedule</h3>
        <Button type="button" variant="outline" size="sm" onClick={onAddRule}>
          Add Call Time
        </Button>
      </div>

      <p className="text-sm text-blue-700">
        <strong>Note:</strong> Each patient can be scheduled for only one call per day.
      </p>

      {callRules.map((rule, index) => (
        <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-4">
          {/* Row 1: Frequency + conditional config */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Frequency</label>
              <select
                value={rule.frequency || 'weekly'}
                onChange={(e) => onCallRuleChange(index, 'frequency', e.target.value)}
                className={selectClassName}
              >
                {FREQUENCY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Interval — shown for daily, weekly, monthly */}
            {(rule.frequency === 'daily' || rule.frequency === 'weekly' || rule.frequency === 'monthly')&& (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Interval
                </label>
                <select
                  value={rule.interval || 1}
                  onChange={(e) => onCallRuleChange(index, 'interval', parseInt(e.target.value))}
                  className={selectClassName}
                >
                  {(INTERVAL_OPTIONS[rule.frequency] || []).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Date picker — only for one-time */}
            {rule.frequency === 'once' && (
               <div>
               <label className="block text-sm font-medium text-gray-700">
                 Date <span className="text-red-500 ml-1">*</span>
               </label>
               <input
                 type="date"
                 value={rule.date || ''}
                 onChange={(e) => onCallRuleChange(index, 'date', e.target.value)}
                 className={`${selectClassName} ${!rule.date && error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
               />
               {!rule.date && error && (
                 <p className="text-xs text-red-500 mt-1">{error}</p>
               )}
             </div>
            )}
          </div>

          {/* Days of week checkboxes — weekly only */}
          {rule.frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Days</label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map(day => {
                  const isSelected = (rule.days || []).includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => handleDayToggle(index, day.value, rule.days || [])}
                      className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {day.label.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
              {(rule.days || []).length === 0 && (
                <p className="text-xs text-red-500 mt-1">Select at least one day</p>
              )}
            </div>
          )}

          {/* Days of month checkboxes — monthly only */}
          {rule.frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Days of Month</label>
              <div className="flex flex-wrap gap-1.5">
                {DAYS_OF_MONTH.map(day => {
                  const isSelected = (rule.days_of_month || []).includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => handleDayOfMonthToggle(index, day.value, rule.days_of_month || [])}
                      className={`w-9 h-9 text-sm rounded-md border transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              {(rule.days_of_month || []).length === 0 && (
                <p className="text-xs text-red-500 mt-1">Select at least one day of the month</p>
              )}
            </div>
          )}

          {/* Row 2: Time selectors (always shown) + Remove */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Hour</label>
              <select
                value={rule.hour}
                onChange={(e) => onCallRuleChange(index, 'hour', parseInt(e.target.value))}
                className={selectClassName}
              >
                {hours.map(hour => (
                  <option key={hour} value={hour}>{hour}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Minute</label>
              <select
                value={rule.minute}
                onChange={(e) => onCallRuleChange(index, 'minute', parseInt(e.target.value))}
                className={selectClassName}
              >
                {minutes.map(minute => (
                  <option key={minute} value={minute}>{minute.toString().padStart(2, '0')}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Period</label>
              <select
                value={rule.period}
                onChange={(e) => onCallRuleChange(index, 'period', e.target.value)}
                className={selectClassName}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>

            <div className="flex items-end">
              {callRules.length > 1 && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => onRemoveRule(index)}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CallRulesSection;